import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { acesso, type PortalConfig } from '@/shared/api/resources';
import type { Branding } from '@/shared/api/types';
import { applyBranding } from '@/shared/branding/applyBranding';

/**
 * Configuracao da tela de acesso antes da sessao.
 *
 * A empresa vem do identificador da rota (/entrar/:empresa) e do dominio em que a
 * aplicacao foi aberta; quem decide qual dos dois vale e o servidor, conforme a
 * instalacao. Sem empresa reconhecida, a tela e a da plataforma, e e a mesma para
 * empresa inexistente e para a que nao publicou a marca.
 */
export function usePortalConfig(tenantSlug?: string) {
  const dominio = typeof window === 'undefined' ? '' : window.location.host;

  const consulta = useQuery({
    queryKey: ['acesso', 'portal', tenantSlug ?? '', dominio],
    queryFn: ({ signal }) =>
      acesso.portalConfig(
        {
          ...(tenantSlug ? { tenantSlug } : {}),
          ...(dominio ? { domain: dominio } : {}),
        },
        signal,
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const portal = consulta.data ?? null;

  // As cores da empresa entram antes do acesso pelo mesmo caminho de depois: as
  // variaveis do elemento raiz, conferidas no formato antes de virar CSS.
  useEffect(() => {
    if (portal?.resolved) {
      applyBranding(marcaDoPortal(portal));
    }
  }, [portal]);

  return { portal, carregando: consulta.isPending };
}

/** Converte a configuracao do portal na identidade visual da aplicacao. */
export function marcaDoPortal(portal: PortalConfig): Branding {
  const comum = portal.layout.common;
  return {
    display_name: portal.display_name,
    logo_url: comum.logo,
    logo_symbol_url: comum.logo_symbol,
    favicon_url: comum.favicon,
    primary_color: comum.primary_color,
    accent_color: comum.accent_color,
    theme_color: comum.theme_color,
    background_color: comum.background_color,
    pwa_name: portal.display_name,
    pwa_short_name: '',
    locale: 'pt-BR',
    timezone: 'America/Sao_Paulo',
  };
}
